/**
 * @fileoverview The main component of the AmplifyChat application.
 * It manages the global state for posts, media, theme, and navigation,
 * and orchestrates the interaction between various child components.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import axios from 'axios';
import { Header } from './components/Header';
import { Composer } from './components/Composer';
const ScheduledPostsList = lazy(() => import('./components/ScheduledPostsList').then(module => ({ default: module.ScheduledPostsList })));
import { PostDetailModal } from './components/PostDetailModal';
const ControlPanel = lazy(() => import('./components/ControlPanel').then(module => ({ default: module.ControlPanel })));
import { Snackbar } from './components/Snackbar';
import { Platform, type Post, type Media, type MediaType, type Account, type User, type Workspace, type Tier, type BillingCycle } from './types';
import { getFirebaseAuth } from './firebase/index';
import { FirebaseError } from 'firebase/app';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  updateProfile,
  fetchSignInMethodsForEmail, 
  GoogleAuthProvider, 
  FacebookAuthProvider, 
  signInWithPopup,
  updateEmail,
  sendPasswordResetEmail
} from 'firebase/auth';
import { generatePostSuggestion, refinePostSuggestion, generateImageDescription } from './services/geminiService';
import { LandingPage } from './components/LandingPage';
const LoginScreen = lazy(() => import('./components/LoginScreen').then(module => ({ default: module.LoginScreen })));
const SignUpScreen = lazy(() => import('./components/SignUpScreen').then(module => ({ default: module.SignUpScreen })));
const ProfileSetupScreen = lazy(() => import('./components/ProfileSetupScreen').then(module => ({ default: module.ProfileSetupScreen })));
const UpgradeModalLazy = lazy(() => import('./components/UpgradeModal').then(module => ({ default: module.UpgradeModal })));
import { FacebookPageSelectionModal } from './components/FacebookPageSelectionModal';
import { createWorkspace, checkWorkspaceNameAvailability } from './api/workspaceApi';
import { updateUserProfile, checkUserAvailability } from './api/users';
import { beginWorkspaceCheckout, openBillingPortal } from './services/billingService';
import { useWorkspaceData } from './hooks/useWorkspaceData';
import { useWorkspaceIntegrations } from './hooks/useWorkspaceIntegrations';
import { useTheme } from './contexts/ThemeContext';
import { useWorkspace } from './contexts/WorkspaceContext';
import { useNotification } from './contexts/NotificationContext';
import { useAuth } from './contexts/AuthContext';
import {
  listenToUserWorkspaces,
  addPostToWorkspace,
  updatePostInWorkspace,
  deletePostFromWorkspace,
  addMediaToWorkspace,
  deleteMediaFromWorkspace,
  updateMediaInWorkspace,
  addAccountToWorkspace,
  deleteAccountFromWorkspace,
  incrementWorkspaceMonthlyPostCount,
  renameWorkspace,
} from './repositories/workspaceApiRepository';
import { deleteMediaByPath, uploadMediaDataUrl } from './services/mediaStorageService';
import { getUsersByIds, upsertUserProfile } from './repositories/userFirestoreRepository';
import { removeInstagramAccountFromWorkspace } from './repositories/workspaceIntegrationRepository';
import {
  startFacebookConnect,
  fetchPendingFacebookPages,
  confirmFacebookPages,
  cancelFacebookConnection,
  type PendingFacebookPage,
} from './services/facebookIntegrationService';


// Define the types for the application's theme and pages.
type Theme = 'light' | 'dark';
type Page = 'main' | 'controlPanel';
type AuthView = 'login' | 'signup';

import { PLAN_LIMITS, DEFAULT_BILLING_CYCLE } from './config/subscriptionConfig';

/**
 * A utility function to get details about a media file from its data URL.
 * @param dataUrl - The base64 data URL of the media.
 * @param type - The type of media ('image' or 'video').
 * @returns A promise that resolves with the media's dimensions and size in bytes.
 */
const getMediaDetails = (dataUrl: string, type: MediaType): Promise<{ width?: number; height?: number; size: number }> => {
  return new Promise((resolve) => {
    // Calculate file size from base64 string.
    const str = dataUrl.split(',')[1];
    const padding = (str.match(/==?$/) || [''])[0].length;
    const size = (str.length * 3 / 4) - padding;

    // If it's an image, load it to get its natural dimensions.
    if (type === 'image') {
      const image = new Image();
      image.onload = () => {
        resolve({
          width: image.naturalWidth,
          height: image.naturalHeight,
          size,
        });
      };
      image.onerror = () => {
        resolve({ size }); // Resolve with size only if dimensions can't be read.
      };
      image.src = dataUrl;
    } else {
       // For videos, getting dimensions is complex client-side, so just resolve the size.
       resolve({ size });
    }
  });
};

// Initial placeholder data for the first workspace created in the app.
const initialPosts: Post[] = [
  {
    id: '1',
    platforms: [Platform.X, Platform.Facebook],
    content: 'Excited to announce our new feature! 🚀\n\nYou can now schedule posts across all your favorite platforms with just one click. Try it out!',
    mediaUrl: null,
    mediaType: null,
    scheduledAt: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000), // 2 days from now
    status: 'scheduled',
    visibility: {
      [Platform.Facebook]: 'public',
    },
  },
  {
    id: '2',
    platforms: [Platform.Instagram],
    content: 'Behind the scenes of our latest project. Hard work pays off! #development #tech #innovation',
    mediaUrl: 'https://images.unsplash.com/photo-1522252234503-e356532cafd5?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    mediaType: 'image',
    scheduledAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
    status: 'posted',
    analytics: {
      reach: 15234,
      engagement: 1256,
      clicks: 45,
    },
  },
  {
    id: '3',
    platforms: [Platform.Facebook, Platform.X],
    content: 'Just pushed a major update! Our app is now faster and more intuitive. Check out the new features and let us know what you think! #update #newfeatures',
    mediaUrl: null,
    mediaType: null,
    scheduledAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    status: 'posted',
    visibility: {
      [Platform.Facebook]: 'friends',
    },
    analytics: {
      reach: 8750,
      engagement: 430,
      clicks: 112,
    }
  }
];

const initialMedia: Media[] = [
  {
    id: 'media_1',
    url: 'https://images.unsplash.com/photo-1522252234503-e356532cafd5?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    type: 'image',
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    description: 'A person coding on a laptop at night.',
    width: 2070,
    height: 1380,
    size: 200000,
  }
];

const initialAccounts: Record<Platform, Account[]> = {
    [Platform.Facebook]: [{ id: `fb_initial`, platform: Platform.Facebook, username: 'AmplifyChat Biz' }],
    [Platform.Instagram]: [],
    [Platform.X]: [
        { id: `x_initial_1`, platform: Platform.X, username: '@AmplifyUser' },
        { id: `x_initial_2`, platform: Platform.X, username: '@AmplifyDev' },
    ],
    [Platform.YouTube]: [],
    [Platform.Mastodon]: [],
    [Platform.Threads]: [],
    [Platform.Bluesky]: [],
    [Platform.TikTok]: [],
};

const createEmptyAccounts = (): Record<Platform, Account[]> => ({
    [Platform.Facebook]: [],
    [Platform.Instagram]: [],
    [Platform.X]: [],
    [Platform.YouTube]: [],
    [Platform.Mastodon]: [],
    [Platform.Threads]: [],
    [Platform.Bluesky]: [],
    [Platform.TikTok]: [],
});

// removed areSubscriptionsEqual

const fallbackDisplayName = (email?: string | null) => {
    if (!email) {
        return 'User';
    }
    const [namePart] = email.split('@');
    return namePart || 'User';
};

const formatAuthError = (error: unknown): string => {
    if (error instanceof FirebaseError) {
        switch (error.code) {
            case 'auth/invalid-email':
                return 'The email address is invalid.';
            case 'auth/user-disabled':
                return 'This account has been disabled.';
            case 'auth/user-not-found':
            case 'auth/wrong-password':
                return 'Incorrect email or password.';
            case 'auth/email-already-in-use':
                return 'An account already exists with this email.';
            case 'auth/weak-password':
                return 'Password should be at least 6 characters.';
            case 'auth/popup-closed-by-user':
                return 'Sign-in was cancelled.';
            case 'auth/cancelled-popup-request':
                return 'Another sign-in attempt is already in progress.';
            case 'auth/popup-blocked':
                return 'Popup blocked by the browser. Please allow popups and try again.';
            case 'auth/network-request-failed':
                return 'Network error. Check your connection and try again.';
            default:
                return 'Authentication failed. Please try again.';
        }
    }
    return 'Something went wrong. Please try again.';
};

const getInitialTier = (
  tier: Tier = 'free',
  billingCycle: BillingCycle = DEFAULT_BILLING_CYCLE
) => {
  return {
    tier,
    billing_period: billingCycle,
    subscription_status: 'active',
  };
};


const App: React.FC = () => {
  const { isDarkTheme, toggleTheme } = useTheme();
  const theme = isDarkTheme ? 'dark' : 'light';
  
  // Use simple react-router-dom agnostic location check, or just default to main since AppRouter renders us
  const [page, setPage] = useState<Page>('main');
  useEffect(() => {
    const p = window.location.pathname.includes('control-panel') ? 'controlPanel' : 'main';
    setPage(p);
  }, []);
  
  const { 
    currentWorkspace, 
    handleSetWorkspace, 
    workspaces, 
    workspacesReady, 
    isProvisioningWorkspace 
  } = useWorkspace();
  
  const { 
    currentUser, 
    backendUser, 
    setBackendUser,
    backendUserLoading, 
    authReady, 
    logout: handleLogout,
    persistUsers,
    setCurrentUser,
    users,
    authView,
    setAuthView,
    showAuthScreen,
    setShowAuthScreen,
    authError,
    setAuthError,
    firebaseUser
  } = useAuth();
  
  const [isUserInitiatedProvision, setIsUserInitiatedProvision] = useState(false);
  
  // Provision workspace logic will no longer be available here natively, but we can mock it
  // or it will just be handled by the profile setup component
  const provisionWorkspace = async () => {};

  const { showNotification } = useNotification();
  const showSnackbar = useCallback((msg: string) => showNotification(msg, 'info'), [showNotification]);


  const {
    profileSetupWorkspaceId,
    profileSetupWorkspace,
    setProfileSetupWorkspace,
    setProfileSetupWorkspaceId,
    setProfileSetupError,
    setProfileSetupSubmitting,
    profileSetupError,
    profileSetupSubmitting,
  } = useWorkspace();


  const profileSetupActive = !!backendUser && backendUser.profile_complete === false;

  const {
    isReady: workspaceDataReady,
    posts,
    mediaLibrary,
    accounts,
    memberRoles,
  } = useWorkspaceData(currentWorkspace);
  
  const {
    facebookPages,
    instagramAccounts,
    loading: integrationsLoading,
  } = useWorkspaceIntegrations(currentWorkspace?.id ?? null);
  
  // UI state
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [postToReuse, setPostToReuse] = useState<Post | null>(null);
  const [mediaToReuse, setMediaToReuse] = useState<Media | null>(null);
  const [activeTab, setActiveTab] = useState<'schedule' | 'archive' | 'media' | 'analytics'>('schedule');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const instagramWarningRef = useRef<Set<string>>(new Set());
  type FacebookSelectionState = {
    isOpen: boolean;
    workspaceId: string | null;
    stateId: string | null;
    loading: boolean;
    pages: PendingFacebookPage[];
    error: string | null;
    redirectUrl: string | null;
    returnPath: string | null;
  };

  const [facebookSelection, setFacebookSelection] = useState<FacebookSelectionState>(() => ({
    isOpen: false,
    workspaceId: null,
    stateId: null,
    loading: false,
    pages: [],
    error: null,
    redirectUrl: null,
    returnPath: null,
  }));

  const activeTier = currentWorkspace?.tier ?? 'free';
  const workspaceForUi = currentWorkspace
    ? {
        ...currentWorkspace,
        memberRoles: {
          ...(currentWorkspace.memberRoles ?? {}),
          ...(memberRoles ?? {}),
        },
      }
    : null;

  const isCurrentUserAdmin = useMemo(() => {
    if (!workspaceForUi || !currentUser) return false;
    if (workspaceForUi.ownerId === currentUser.id) return true;
    return workspaceForUi.adminIds?.includes(currentUser.id) ?? false;
  }, [workspaceForUi, currentUser]);

  const isCurrentUserOwner = useMemo(() => {
    if (!workspaceForUi || !currentUser) return false;
    return workspaceForUi.ownerId === currentUser.id;
  }, [workspaceForUi, currentUser]);
  const currentMemberRole = useMemo(() => {
    if (!workspaceForUi || !currentUser) return 'viewer';
    const roles = workspaceForUi.memberRoles ?? {};
    return roles[currentUser.id] ?? (isCurrentUserOwner ? 'owner' : isCurrentUserAdmin ? 'admin' : 'publisher');
  }, [workspaceForUi, currentUser, isCurrentUserOwner, isCurrentUserAdmin]);
  const canPublishWithoutApproval = currentMemberRole === 'owner' || currentMemberRole === 'admin';

  useEffect(() => {
    if (!isCurrentUserAdmin) {
      instagramWarningRef.current.clear();
      return;
    }
    if (instagramAccounts.length === 0) {
      return;
    }
    const needsAttention = instagramAccounts.filter(account => account.requiresReauthorization);
    if (needsAttention.length === 0) {
      return;
    }
    const unwarned = needsAttention.filter(account => !instagramWarningRef.current.has(account.id));
    if (unwarned.length === 0) {
      return;
    }
    unwarned.forEach(account => instagramWarningRef.current.add(account.id));
    const names = unwarned.map(account => `@${account.username}`).join(', ');
    showSnackbar(`Reconnect Instagram tokens for ${names} in the Control Panel.`);
  }, [instagramAccounts, isCurrentUserAdmin, showSnackbar]);

  const handleCheckUsernameAvailability = useCallback(async (username: string) => {
    const response = await checkUserAvailability({ username });
    return response.username_available !== false;
  }, []);

  const handleCheckEmailAvailability = useCallback(async (email: string) => {
    const response = await checkUserAvailability({ email });
    return response.email_available !== false;
  }, []);

  const handleCheckBrandAvailability = useCallback(async (name: string) => {
    const response = await checkWorkspaceNameAvailability(name);
    return response.name_available !== false;
  }, []);

  const handleNavigation = useCallback((targetPage: Page) => {
    setPage(targetPage);
    const nextPath = targetPage === 'controlPanel' ? '/control-panel' : '/dashboard';
    if (window.location.pathname !== nextPath) {
      window.history.pushState(null, '', nextPath);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleProfileSetupSubmit = useCallback(async (payload: {
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    dob: string;
    brand_name: string;
  }) => {
    if (!currentUser) return;
    setProfileSetupError(null);
    setProfileSetupSubmitting(true);
    try {
      // NOTE: backendUser should be handled by AuthContext now, but we just trigger refetch via setting state locally 
      const updated = await updateUserProfile({
        username: payload.username,
        first_name: payload.first_name,
        last_name: payload.last_name,
        email: payload.email,
        dob: payload.dob,
      });

      const workspaceId = profileSetupWorkspaceId ?? profileSetupWorkspace?.id ?? workspaces[0]?.id ?? null;
      if (!workspaceId) {
        throw new Error('Workspace not available');
      }
      await renameWorkspace(workspaceId, payload.brand_name);

      const updatedLocal: User = {
        ...currentUser,
        name: `${payload.first_name} ${payload.last_name}`.trim(),
        email: payload.email,
        username: payload.username,
        first_name: payload.first_name,
        last_name: payload.last_name,
        dob: payload.dob,
        profile_complete: true,
      };
      setBackendUser({
        ...updated,
        profile_complete: true,
      });
      persistUsers(prev => prev.map(u => (u.id === updatedLocal.id ? updatedLocal : u)));
      setCurrentUser(updatedLocal);

      if (profileSetupWorkspace) {
        const updatedWorkspace = { ...profileSetupWorkspace, name: payload.brand_name };
        setProfileSetupWorkspace(updatedWorkspace);
        handleSetWorkspace(updatedWorkspace);
      } else {
        const existingWorkspace = workspaces.find(ws => ws.id === workspaceId);
        if (existingWorkspace) {
          handleSetWorkspace({ ...existingWorkspace, name: payload.brand_name });
        }
      }
      handleNavigation('main');
    } catch (error) {
      console.error('Failed to complete profile setup:', error);
      if (axios.isAxiosError(error)) {
        const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
        if (detail) {
          setProfileSetupError(detail);
          return;
        }
      }
      setProfileSetupError('Unable to save your profile. Please try again.');
    } finally {
      setProfileSetupSubmitting(false);
    }
  }, [
    currentUser, 
    handleSetWorkspace, 
    persistUsers, 
    profileSetupWorkspace, 
    profileSetupWorkspaceId, 
    workspaces,
    setProfileSetupError,
    setProfileSetupSubmitting,
    setCurrentUser,
    setProfileSetupWorkspace,
    setBackendUser,
    handleNavigation
  ]);

  const deriveReturnPath = useCallback((redirect: string | null, fallback: string | null) => {
    if (fallback) {
      return fallback;
    }
    if (!redirect) {
      return null;
    }
    try {
      const parsed = new URL(redirect, window.location.origin);
      return parsed.searchParams.get('returnTo');
    } catch (error) {
      console.error('Failed to parse return path from redirect URL', error);
      return null;
    }
  }, []);

  const resetFacebookSelection = useCallback(() => {
    setFacebookSelection({
      isOpen: false,
      workspaceId: null,
      stateId: null,
      loading: false,
      pages: [],
      error: null,
      redirectUrl: null,
      returnPath: null,
    });
  }, []);

  const openFacebookSelection = useCallback((workspaceId: string, stateId: string, initialReturnPath: string | null) => {
    setFacebookSelection({
      isOpen: true,
      workspaceId,
      stateId,
      loading: true,
      pages: [],
      error: null,
      redirectUrl: null,
      returnPath: initialReturnPath,
    });

    void fetchPendingFacebookPages(workspaceId, stateId)
      .then(result => {
        setFacebookSelection(prev => {
          if (!prev.isOpen || prev.stateId !== stateId) {
            return prev;
          }
          const nextReturnPath = deriveReturnPath(result.redirect ?? null, prev.returnPath);
          return {
            ...prev,
            loading: false,
            pages: result.pages,
            redirectUrl: result.redirect ?? null,
            returnPath: nextReturnPath,
          };
        });
      })
      .catch(error => {
        const message = error instanceof Error
          ? error.message
          : 'Unable to load Facebook Pages. Please try again.';
        setFacebookSelection(prev => ({
          ...prev,
          loading: false,
          error: message,
        }));
        showSnackbar(message);
      });
  }, [deriveReturnPath, showSnackbar]);

  const handleStartFacebookConnect = useCallback(() => {
    if (!currentWorkspace || !currentUser) {
      showSnackbar('Finish setup before connecting Facebook.');
      return;
    }
    const returnPath = '/control-panel';
    startFacebookConnect(currentWorkspace.id, currentUser.id, returnPath);
  }, [currentWorkspace, currentUser, showSnackbar]);

  const handleFacebookConfirm = useCallback(async (selectedPageIds: string[]) => {
    if (!facebookSelection.workspaceId || !facebookSelection.stateId) {
      throw new Error('Missing Facebook selection context.');
    }
    try {
      await confirmFacebookPages(facebookSelection.workspaceId, facebookSelection.stateId, selectedPageIds);
      showSnackbar('Facebook Pages connected successfully.');
      const targetPath = facebookSelection.returnPath ?? '/control-panel';
      window.history.replaceState(null, '', targetPath);
      if (targetPath === '/control-panel') {
        handleNavigation('controlPanel');
      }
      resetFacebookSelection();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to connect selected Pages.';
      setFacebookSelection(prev => ({ ...prev, error: message }));
      throw error;
    }
  }, [facebookSelection, handleNavigation, resetFacebookSelection, showSnackbar]);

  const handleFacebookCancel = useCallback(async () => {
    if (!facebookSelection.workspaceId || !facebookSelection.stateId) {
      window.history.replaceState(null, '', facebookSelection.returnPath ?? '/');
      resetFacebookSelection();
      return;
    }
    try {
      await cancelFacebookConnection(facebookSelection.workspaceId, facebookSelection.stateId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to cancel Facebook connection.';
      setFacebookSelection(prev => ({ ...prev, error: message }));
      throw error;
    }
    const targetPath = facebookSelection.returnPath ?? '/control-panel';
    window.history.replaceState(null, '', targetPath);
    if (targetPath === '/control-panel') {
      handleNavigation('controlPanel');
    }
    resetFacebookSelection();
  }, [facebookSelection, handleNavigation, resetFacebookSelection]);

  const handleFacebookDismiss = useCallback(() => {
    void handleFacebookCancel().catch(error => {
      console.error('Failed to cancel Facebook connection', error);
    });
  }, [handleFacebookCancel]);


  // --- User, Workspace, and Data Management ---


  
  useEffect(() => {
    if (!currentUser) {
      return;
    }
    const path = window.location.pathname;
    if (path === '/control-panel') {
      setPage('controlPanel');
    } else {
      setPage('main');
    }
  }, [currentUser, setPage]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    if (window.location.pathname === '/') {
      window.history.replaceState(null, '', '/dashboard');
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentWorkspace) {
      return;
    }
    const members = currentWorkspace.members || [];
    const missingIds = members.filter(memberId => !users.some(user => user.id === memberId));
    if (missingIds.length === 0) {
      return;
    }
    getUsersByIds(missingIds)
      .then(profiles => {
        if (profiles.length === 0) {
          return;
        }
        persistUsers(prev => {
          const map = new Map<string, User>(prev.map(user => [user.id, user]));
          profiles.forEach(profile => {
            const existing = map.get(profile.id);
            const workspacesSet = new Set<string>(existing?.workspaces ?? []);
            workspacesSet.add(currentWorkspace.id);
            const updated: User = {
              id: profile.id,
              name: profile.name,
              email: profile.email,
              workspaces: Array.from(workspacesSet),
              password: existing?.password,
            };
            map.set(profile.id, updated);
          });
          return Array.from(map.values());
        });
      })
      .catch(error => {
        console.error('Failed to load member profiles:', error);
      });
  }, [currentWorkspace, users, persistUsers]);

  useEffect(() => {
    if (!authReady || !currentUser) {
      return;
    }
    if (facebookSelection.isOpen) {
      return;
    }

    const { pathname, search } = window.location;
    if (pathname !== '/integrations/facebook/select') {
      return;
    }

    const params = new URLSearchParams(search);
    const stateId = params.get('stateId');
    const workspaceIdParam = params.get('workspaceId');
    const returnTo = params.get('returnTo');
    const workspaceId = workspaceIdParam ?? currentWorkspace?.id ?? null;

    if (!stateId || !workspaceId) {
      showSnackbar('Facebook connection could not be resumed. Please restart the flow.');
      window.history.replaceState(null, '', '/control-panel');
      handleNavigation('controlPanel');
      return;
    }

    openFacebookSelection(workspaceId, stateId, returnTo);
  }, [authReady, currentUser, currentWorkspace, facebookSelection.isOpen, openFacebookSelection, showSnackbar, handleNavigation]);


  
  const handleUpgradeWorkspace = useCallback(async (
    targetTier: Tier = 'professional',
    billingCycle: BillingCycle = DEFAULT_BILLING_CYCLE
  ) => {
    if (!currentWorkspace || !workspaceDataReady) return;
    if (!isCurrentUserAdmin) {
      showSnackbar('Only admins can upgrade the plan.');
      return;
    }
    if (!isCurrentUserAdmin) {
      showSnackbar('Only admins can upgrade the plan.');
      return;
    }
    
    if (activeTier === targetTier) {
      setShowUpgradeModal(false);
      showSnackbar('You are already on this plan.');
      return;
    }
    try {
      const returnUrl = window.location.href;
      const checkoutUrl = await beginWorkspaceCheckout({
        workspaceId: currentWorkspace.id,
        plan: targetTier,
        billingCycle,
        returnUrl,
      });
      window.location.assign(checkoutUrl);
    } catch (error) {
      console.error('Failed to start checkout:', error);
      showSnackbar('Unable to start upgrade checkout. Please try again.');
    }
  }, [currentWorkspace, workspaceDataReady, activeTier, isCurrentUserAdmin, showSnackbar]);

  const handleDowngradeWorkspace = useCallback(async () => {
    if (!currentWorkspace || !workspaceDataReady) return;
    if (!isCurrentUserAdmin) {
      showSnackbar('Only admins can manage billing.');
      return;
    }
    if (!isCurrentUserAdmin) {
      showSnackbar('Only admins can manage billing.');
      return;
    }
    
    if (activeTier === 'free') {
      showSnackbar('You are already on the Free plan.');
      return;
    }
    try {
      const portalUrl = await openBillingPortal(currentWorkspace.id, window.location.href);
      window.location.assign(portalUrl);
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      showSnackbar('Unable to open billing portal. Please try again.');
    }
  }, [currentWorkspace, workspaceDataReady, activeTier, isCurrentUserAdmin, showSnackbar]);

  const [authLoading, setAuthLoading] = useState(false);

  
  const handleEmailSignIn = useCallback(async (email: string, password: string) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const auth = await getFirebaseAuth();
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setAuthError(formatAuthError(error));
    } finally {
      setAuthLoading(false);
    }
  }, [setAuthError]);

  const handleEmailSignUp = useCallback(async ({ name, email, password }: any) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const auth = await getFirebaseAuth();
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      if (credential.user) {
        await updateProfile(credential.user, { displayName: name });
      }
    } catch (error) {
      setAuthError(formatAuthError(error));
    } finally {
      setAuthLoading(false);
    }
  }, [setAuthError]);

  const handleCheckExistingAccount = useCallback(async (email: string) => {
    const auth = await getFirebaseAuth();
    const methods = await fetchSignInMethodsForEmail(auth, email);
    return methods.length > 0;
  }, []);

  const handleGoogleSignIn = useCallback(async (mode: 'login' | 'signup' = 'login') => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const auth = await getFirebaseAuth();
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
    } catch (error) {
      setAuthError(formatAuthError(error));
    } finally {
      setAuthLoading(false);
    }
  }, [setAuthError]);

  const handleFacebookSignIn = useCallback(async (mode: 'login' | 'signup' = 'login') => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const auth = await getFirebaseAuth();
      const provider = new FacebookAuthProvider();
      provider.setCustomParameters({ display: 'popup' });
      await signInWithPopup(auth, provider);
    } catch (error) {
      setAuthError(formatAuthError(error));
    } finally {
      setAuthLoading(false);
    }
  }, [setAuthError]);




  const handleSchedulePost = useCallback(async (postData: Omit<Post, 'id' | 'status'>, mediaDescription: string | null) => {
    if (!currentWorkspace || !workspaceDataReady) return;

    if (!currentWorkspace || !workspaceDataReady) return;

    // TODO: restore post limiting based on tier if needed
    // const postLimit = PLAN_LIMITS[activeTier].posts;

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString();

    // let currentPostCount = usage.posts.count;
    // if (usage.posts.resetDate !== firstDayOfMonth) {
    //   currentPostCount = 0;
    // }

    // if (currentPostCount >= postLimit) {
    //   setShowUpgradeModal(true);
    //   return;
    // }

    let createdPostId: string | null = null;
    try {
      let mediaUrlForPost = postData.mediaUrl;
      const mediaTypeForPost = postData.mediaType;

      if (mediaUrlForPost && mediaTypeForPost) {
        const isDataUrl = mediaUrlForPost.startsWith('data:');
        const isExistingMedia = mediaLibrary.some(m => m.url === mediaUrlForPost);

        if (isDataUrl && !isExistingMedia) {
          const [details, uploadResult, description] = await Promise.all([
            getMediaDetails(mediaUrlForPost, mediaTypeForPost),
            uploadMediaDataUrl(currentWorkspace.id, mediaUrlForPost),
            (async () => {
              if (mediaDescription !== null) {
                return mediaDescription;
              }
              if (mediaTypeForPost === 'image') {
                const base64 = mediaUrlForPost.split(',')[1];
                const mimeType = mediaUrlForPost.substring(mediaUrlForPost.indexOf(':') + 1, mediaUrlForPost.indexOf(';'));
                return generateImageDescription(base64, mimeType);
              }
              return 'Video content';
            })(),
          ]);

          const mediaPayload: Omit<Media, 'id'> = {
            url: uploadResult.downloadUrl,
            type: mediaTypeForPost,
            createdAt: new Date(),
            description: description || '',
            width: details.width,
            height: details.height,
            size: details.size,
            storagePath: uploadResult.storagePath,
          };
          await addMediaToWorkspace(currentWorkspace.id, mediaPayload);
          mediaUrlForPost = uploadResult.downloadUrl;
        }
      }

      const status = postData.scheduledAt > new Date() ? 'scheduled' : 'posted';
      createdPostId = await addPostToWorkspace(currentWorkspace.id, {
        ...postData,
        mediaUrl: mediaUrlForPost,
        mediaType: mediaTypeForPost,
        scheduledAt: postData.scheduledAt,
        status,
      });

      await incrementWorkspaceMonthlyPostCount(currentWorkspace.id, firstDayOfMonth);

      showSnackbar(status === 'scheduled' ? 'Post scheduled successfully!' : 'Post published successfully!');
    } catch (error) {
      if (createdPostId) {
        try {
          await deletePostFromWorkspace(currentWorkspace.id, createdPostId);
        } catch (cleanupError) {
          console.error('Failed to roll back post after error:', cleanupError);
        }
      }
      if (error instanceof Error && error.message === 'post-limit-exceeded') {
        setShowUpgradeModal(true);
        showSnackbar('Post limit reached. Upgrade your plan to keep publishing.');
        return;
      }
      console.error('Failed to schedule post:', error);
      showSnackbar('Failed to schedule post. Please try again.');
    }
  }, [currentWorkspace, workspaceDataReady, activeTier, mediaLibrary, generateImageDescription, showSnackbar, setShowUpgradeModal]);

  const handleDeletePost = useCallback(async (postId: string) => {
    if (!currentWorkspace) return;
    if (!window.confirm('Are you sure you want to delete this post permanently? This action cannot be undone.')) {
      return;
    }
    try {
      await deletePostFromWorkspace(currentWorkspace.id, postId);
      showSnackbar('Post deleted.');
    } catch (error) {
      console.error('Failed to delete post:', error);
      showSnackbar('Failed to delete post.');
    }
  }, [currentWorkspace, showSnackbar]);

  const handleUpdatePost = useCallback(async (postId: string, updates: Partial<Pick<Post, 'content' | 'platforms' | 'visibility'>>) => {
    if (!currentWorkspace) return;
    try {
      await updatePostInWorkspace(currentWorkspace.id, postId, updates);
      showSnackbar('Post updated successfully.');
    } catch (error) {
      console.error('Failed to update post:', error);
      showSnackbar('Failed to update post.');
    }
  }, [currentWorkspace, showSnackbar]);
  
  const handleArchivePost = useCallback(async (postId: string) => {
    if (!currentWorkspace) return;
    try {
      await updatePostInWorkspace(currentWorkspace.id, postId, { status: 'archived' });
      showSnackbar('Post archived.');
    } catch (error) {
      console.error('Failed to archive post:', error);
      showSnackbar('Failed to archive post.');
    }
  }, [currentWorkspace, showSnackbar]);

  const handleReschedulePost = useCallback(async (postId: string, newDate: Date) => {
    if (!currentWorkspace) return;
    try {
      await updatePostInWorkspace(currentWorkspace.id, postId, { scheduledAt: newDate });
      showSnackbar('Post rescheduled.');
    } catch (error) {
      console.error('Failed to reschedule post:', error);
      showSnackbar('Failed to reschedule post.');
    }
  }, [currentWorkspace, showSnackbar]);
  
  const handleReusePost = (post: Post) => {
    setPostToReuse(post);
    handleNavigation('main');
    showSnackbar('Post content copied to composer.');
  };
  
  const handleDeleteMedia = useCallback(async (mediaId: string) => {
    if (!currentWorkspace) return;
    if (!window.confirm('Are you sure you want to delete this media? This cannot be undone.')) {
      return;
    }
    try {
      const media = mediaLibrary.find(item => item.id === mediaId);
      if (media?.storagePath) {
        await deleteMediaByPath(media.storagePath);
      }
      await deleteMediaFromWorkspace(currentWorkspace.id, mediaId);
      showSnackbar('Media deleted.');
    } catch (error) {
      console.error('Failed to delete media:', error);
      showSnackbar('Failed to delete media.');
    }
  }, [currentWorkspace, mediaLibrary, showSnackbar]);

  const handleBulkDeleteMedia = useCallback(async (mediaIds: string[]) => {
    if (!currentWorkspace || mediaIds.length === 0) return;
    try {
      await Promise.all(mediaIds.map(async mediaId => {
        const media = mediaLibrary.find(item => item.id === mediaId);
        if (media?.storagePath) {
          await deleteMediaByPath(media.storagePath);
        }
        await deleteMediaFromWorkspace(currentWorkspace.id, mediaId);
      }));
      showSnackbar(`${mediaIds.length} media item(s) deleted.`);
    } catch (error) {
      console.error('Failed to delete media items:', error);
      showSnackbar('Failed to delete selected media.');
    }
  }, [currentWorkspace, mediaLibrary, showSnackbar]);
  
  const handleSelectMediaForReuse = (media: Media) => {
    setMediaToReuse(media);
    handleNavigation('main');
    setActiveTab('schedule');
    showSnackbar('Media added to composer.');
  };
  
  const handleUpdateMediaDescription = useCallback(async (mediaId: string, newDescription: string) => {
    if (!currentWorkspace) return;
    try {
      await updateMediaInWorkspace(currentWorkspace.id, mediaId, { description: newDescription });
      showSnackbar('Media description updated.');
    } catch (error) {
      console.error('Failed to update media description:', error);
      showSnackbar('Failed to update media description.');
    }
  }, [currentWorkspace, showSnackbar]);

  const handleConnectAccount = useCallback(async (platform: Platform, username: string) => {
      if (!currentWorkspace || !workspaceDataReady) return;
      if (!isCurrentUserAdmin) {
        const message = 'Only admins can connect accounts.';
        showSnackbar(message);
        throw new Error(message);
      }

      const currentAccountCount = Object.values(accounts).flat().length;
      const limit = PLAN_LIMITS[activeTier].channels; // was accounts

      if (currentAccountCount >= limit) {
          setShowUpgradeModal(true);
          throw new Error('Account limit reached.');
      }
      
      const newAccount: Account = {
        id: `${platform.slice(0, 2).toLowerCase()}_${Date.now()}`,
        platform,
        username,
      };
      try {
        await addAccountToWorkspace(currentWorkspace.id, newAccount);
        showSnackbar(`${platform} account connected!`);
      } catch (error) {
        console.error('Failed to connect account:', error);
        const message = 'Failed to connect account.';
        showSnackbar(message);
        throw error instanceof Error ? error : new Error(message);
      }
  }, [currentWorkspace, workspaceDataReady, accounts, activeTier, isCurrentUserAdmin, showSnackbar]);

  const handleDisconnectAccount = useCallback(async (accountId: string) => {
      if (!currentWorkspace) return;
      if (!isCurrentUserAdmin) {
        showSnackbar('Only admins can disconnect accounts.');
        return;
      }
      if (!window.confirm('Are you sure you want to disconnect this account?')) {
        return;
      }
      try {
        await deleteAccountFromWorkspace(currentWorkspace.id, accountId);
        showSnackbar('Account disconnected.');
      } catch (error) {
        console.error('Failed to disconnect account:', error);
        showSnackbar('Failed to disconnect account.');
      }
  }, [currentWorkspace, isCurrentUserAdmin, showSnackbar]);

  const handleDisconnectInstagramAccount = useCallback(async (accountId: string) => {
      if (!currentWorkspace) return;
      if (!isCurrentUserAdmin) {
        showSnackbar('Only admins can disconnect accounts.');
        return;
      }
      const confirmMessage = 'Disconnect this Instagram account? Scheduled Instagram posts targeting it will fail until it is reconnected.';
      if (!window.confirm(confirmMessage)) {
        return;
      }
      try {
        await removeInstagramAccountFromWorkspace(currentWorkspace.id, accountId);
        showSnackbar('Instagram account disconnected.');
      } catch (error) {
        console.error('Failed to disconnect Instagram account:', error);
        showSnackbar('Failed to disconnect Instagram account.');
      }
  }, [currentWorkspace, isCurrentUserAdmin, showSnackbar]);

  const handleUpdateEmailAddress = useCallback(async (newEmail: string) => {
    const trimmedEmail = newEmail.trim();
    if (!trimmedEmail) {
      throw new Error('Email address is required.');
    }
    if (!/\S+@\S+\.\S+/.test(trimmedEmail)) {
      throw new Error('Enter a valid email address.');
    }
    try {
      const auth = await getFirebaseAuth();
      if (!auth.currentUser) {
        throw new Error('You must be signed in to update your email.');
      }
      await updateEmail(auth.currentUser, trimmedEmail);
      setCurrentUser(currentUser ? { ...currentUser, email: trimmedEmail } : null);
      persistUsers(prev => prev.map(user => (user.id === auth.currentUser?.uid ? { ...user, email: trimmedEmail } : user)));
      upsertUserProfile({ id: auth.currentUser.uid, name: auth.currentUser.displayName ?? trimmedEmail, email: trimmedEmail }).catch(error => {
        console.error('Failed to persist updated email profile:', error);
      });
      showSnackbar('Email updated. Please check your inbox to verify the new address.');
    } catch (error) {
      console.error('Failed to update email:', error);
      throw (error instanceof Error ? error : new Error('Failed to update email address.'));
    }
  }, [persistUsers, showSnackbar, upsertUserProfile]);

  const handleSendPasswordReset = useCallback(async () => {
    if (!currentUser?.email) {
      throw new Error('No email found on your profile.');
    }
    try {
      const auth = await getFirebaseAuth();
      await sendPasswordResetEmail(auth, currentUser.email);
      showSnackbar('Password reset email sent. Check your inbox.');
    } catch (error) {
      console.error('Failed to send password reset email:', error);
      throw (error instanceof Error ? error : new Error('Failed to send password reset email.'));
    }
  }, [currentUser?.email, showSnackbar]);

  
  // --- Render Logic ---

  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 text-slate-600 dark:text-gray-300">
        Loading...
      </div>
    );
  }

  if (!currentUser) {
    if (!showAuthScreen) {
        return <LandingPage 
            onLoginClick={() => { setAuthError(null); setAuthView('login'); setShowAuthScreen(true); }}
            onSignUpClick={() => { setAuthError(null); setAuthView('signup'); setShowAuthScreen(true); }}
        />;
    }
    
    if (authView === 'signup') {
        return (
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 text-slate-600 dark:text-gray-300">Loading...</div>}>
            <SignUpScreen 
                onCreateUser={handleEmailSignUp} 
                onSwitchToLogin={() => { setAuthError(null); setAuthView('login'); }}
                onSignUpWithGoogle={() => handleGoogleSignIn('signup')}
                onCheckExistingAccount={handleCheckExistingAccount}
                loading={authLoading}
                error={authError}
            />
          </Suspense>
        );
    }

    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 text-slate-600 dark:text-gray-300">Loading...</div>}>
        <LoginScreen 
            onLogin={handleEmailSignIn} 
            onLoginWithGoogle={() => handleGoogleSignIn('login')}
            onLoginWithFacebook={() => handleFacebookSignIn('login')}
            onSwitchToSignUp={() => { setAuthError(null); setAuthView('signup'); }}
            loading={authLoading}
            error={authError}
        />
      </Suspense>
    );
  }

  if (backendUserLoading || !backendUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 text-slate-600 dark:text-gray-300">
        Loading your profile...
      </div>
    );
  }

  if (profileSetupActive) {
    const providerIds = firebaseUser?.providerData?.map(p => p.providerId) ?? [];
    const emailEditable = !providerIds.includes('google.com');
    const nameParts = (currentUser.name ?? '').trim().split(' ');
    const initialFirstName = nameParts[0] ?? '';
    const initialLastName = nameParts.slice(1).join(' ');

    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 text-slate-600 dark:text-gray-300">Loading...</div>}>
        <ProfileSetupScreen
          initialEmail={currentUser.email}
          emailEditable={emailEditable}
          initialFirstName={initialFirstName}
          initialLastName={initialLastName}
          onSubmit={handleProfileSetupSubmit}
          onCheckUsername={handleCheckUsernameAvailability}
          onCheckEmail={handleCheckEmailAvailability}
          onCheckBrandName={handleCheckBrandAvailability}
          loading={profileSetupSubmitting}
          error={profileSetupError}
        />
      </Suspense>
    );
  }

  if (!currentWorkspace) {
    if (!workspacesReady) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 text-slate-600 dark:text-gray-300">
          Loading your brand...
        </div>
      );
    }
    if (isProvisioningWorkspace && isUserInitiatedProvision) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 text-slate-600 dark:text-gray-300">
          Setting up your brand...
        </div>
      );
    }
    if (workspaces.length === 0) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 text-slate-700 dark:text-gray-300 text-center px-4">
          <div className="max-w-md">
            <p className="text-2xl font-semibold text-slate-900 dark:text-white">Welcome to AmplifyChat</p>
            <p className="mt-2 text-sm text-slate-600 dark:text-gray-400">
              You&apos;re ready to go. Create your first brand when you&apos;re ready.
            </p>
            <button
              type="button"
              onClick={() => {
                setIsUserInitiatedProvision(true);
                void provisionWorkspace();
              }}
              className="mt-5 inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
            >
              Create My Brand
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 text-slate-600 dark:text-gray-300">
        Loading your brand...
      </div>
    );
  }

  return (
    <div className={`theme-${theme} bg-slate-50 dark:bg-gray-900 min-h-screen text-slate-800 dark:text-gray-200 transition-colors duration-300`}>
      <Header 
        theme={theme} 
        toggleTheme={toggleTheme} 
        onNavigate={handleNavigation} 
        currentUser={currentUser}
        onLogout={handleLogout}
      />
      <main className="container mx-auto p-4 md:p-6">
        {page === 'main' ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-start">
            <div className="lg:col-span-6 xl:col-span-5">
              <Composer
                onSchedulePost={handleSchedulePost}
                generatePostSuggestion={generatePostSuggestion}
                refinePostSuggestion={refinePostSuggestion}
                postToReuse={postToReuse}
                onClearReusedPost={() => setPostToReuse(null)}
                mediaToReuse={mediaToReuse}
                onClearReusedMedia={() => setMediaToReuse(null)}
                showSnackbar={showSnackbar}
                facebookPages={facebookPages}
                facebookPagesLoading={integrationsLoading}
                onRequestFacebookConnect={handleStartFacebookConnect}
                instagramAccounts={instagramAccounts}
                instagramAccountsLoading={integrationsLoading}
                onRequestInstagramConnect={handleStartFacebookConnect}
                canPublishWithoutApproval={canPublishWithoutApproval}
              />
            </div>
            <div className="lg:col-span-6 xl:col-span-7">
              <Suspense fallback={<div className="py-12 text-center text-sm text-slate-500">Loading data...</div>}>
                <ScheduledPostsList
                  posts={posts}
                  onDeletePost={handleDeletePost}
                  onSelectPost={setSelectedPost}
                  onReschedulePost={handleReschedulePost}
                  onReusePost={handleReusePost}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  mediaLibrary={mediaLibrary}
                  onDeleteMedia={handleDeleteMedia}
                  onSelectMediaForReuse={handleSelectMediaForReuse}
                  onBulkDeleteMedia={handleBulkDeleteMedia}
                  onUpdateMediaDescription={handleUpdateMediaDescription}
                  subscriptionPlan={activeTier}
                />
              </Suspense>
            </div>
          </div>
        ) : (
          <Suspense fallback={<div className="py-12 text-center">Loading control panel...</div>}>
            <ControlPanel 
              accounts={accounts}
              onConnect={handleConnectAccount}
              onDisconnect={handleDisconnectAccount}
              workspace={(workspaceForUi ?? currentWorkspace)!}
              currentUser={currentUser}
              isAdmin={isCurrentUserAdmin}
              onUpgradeWorkspace={handleUpgradeWorkspace}
              onManageSubscription={handleDowngradeWorkspace}
              onConnectFacebook={handleStartFacebookConnect}
              facebookPages={facebookPages}
              facebookPagesLoading={integrationsLoading}
              instagramAccounts={instagramAccounts}
              instagramAccountsLoading={integrationsLoading}
              onDisconnectInstagramAccount={handleDisconnectInstagramAccount}
              onUpdateEmail={handleUpdateEmailAddress}
              onSendPasswordReset={handleSendPasswordReset}
            />
          </Suspense>
        )}
      </main>
      <footer className="border-t border-slate-200 dark:border-gray-800 mt-10">
        <div className="container mx-auto px-4 md:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500 dark:text-gray-400">
          <span>&copy; {new Date().getFullYear()} AmplifyChat. All Rights Reserved.</span>
          <a
            href="/privacy.html"
            className="text-blue-600 dark:text-blue-400 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </div>
      </footer>
      <PostDetailModal
        post={selectedPost}
        onClose={() => setSelectedPost(null)}
        onUpdatePost={handleUpdatePost}
        onArchivePost={handleArchivePost}
      />

      <FacebookPageSelectionModal
        isOpen={facebookSelection.isOpen}
        pages={facebookSelection.pages}
        loading={facebookSelection.loading}
        error={facebookSelection.error}
        onConfirm={handleFacebookConfirm}
        onCancel={handleFacebookCancel}
        onDismiss={handleFacebookDismiss}
      />
      <Suspense fallback={null}>
        <UpgradeModalLazy 
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          onUpgrade={handleUpgradeWorkspace}
        />
      </Suspense>
    </div>
  );
};

export default App;
